import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CalendarDays, Plus, ChevronLeft, ChevronRight, Loader2,
  CheckCircle2, Circle, Clock, Users, Bell, FileText, 
  CalendarCheck, Trash2, Edit2, Paperclip, Link2, X,
  ExternalLink, Upload, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { calendarApi, eventsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const EVENT_TYPES = {
  event: { label: 'Evento Público', icon: CalendarCheck, color: '#FF6B6B' },
  task: { label: 'Tarea', icon: CheckCircle2, color: '#4ECDC4' },
  meeting: { label: 'Reunión', icon: Users, color: '#45B7D1' },
  reminder: { label: 'Recordatorio', icon: Bell, color: '#FFD93D' },
  note: { label: 'Nota', icon: FileText, color: '#A78BFA' },
};

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const CalendarPage = () => {
  const { hasPermission } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [publicEvents, setPublicEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month, week, list
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'task',
    start_date: '',
    end_date: '',
    all_day: false,
    color: '#4ECDC4',
    attachments: [],
  });
  const [newAttachment, setNewAttachment] = useState({ name: '', url: '', type: 'link' });

  // Calculate calendar data
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Previous month padding
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }
    
    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }
    
    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }
    
    return days;
  }, [currentDate]);

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      // Load all calendar events and public events (no date filter)
      const [calendarRes, publicRes] = await Promise.all([
        calendarApi.getEvents(), // Get all events
        eventsApi.getPublic(true).catch(() => ({ data: [] }))
      ]);
      
      setEvents(calendarRes.data || []);
      setPublicEvents(publicRes.data || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if an event is in the past
  const isEventPast = (event) => {
    const eventDate = new Date(event.end_date || event.start_date || event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dateTime = new Date(dateStr).getTime();
    
    // Get calendar events
    const calendarEvents = events.filter(e => {
      const startDate = new Date(e.start_date?.split('T')[0]).getTime();
      const endDate = e.end_date ? new Date(e.end_date?.split('T')[0]).getTime() : startDate;
      return dateTime >= startDate && dateTime <= endDate;
    });
    
    // Get public events (platform events)
    const platformEvents = publicEvents.filter(e => {
      if (!e.start_date && !e.date) return false;
      const eventDateStr = e.start_date || e.date;
      const eventDate = new Date(eventDateStr.split('T')[0]).getTime();
      return eventDate === dateTime;
    }).map(e => ({
      ...e,
      event_type: 'event',
      title: e.title,
      color: '#FF6B6B',
      isPublicEvent: true,
      start_date: e.start_date || e.date,
      image_url: e.image_url,
      venue_name: e.venue_name,
      time: e.time
    }));
    
    return [...calendarEvents, ...platformEvents];
  };

  // Check if event spans multiple days
  const isMultiDayEvent = (event) => {
    if (!event.start_date || !event.end_date) return false;
    const start = new Date(event.start_date.split('T')[0]);
    const end = new Date(event.end_date.split('T')[0]);
    return start.getTime() !== end.getTime();
  };

  // Get position of date within multi-day event
  const getEventPosition = (event, date) => {
    const dateStr = date.toISOString().split('T')[0];
    const startStr = event.start_date?.split('T')[0];
    const endStr = event.end_date?.split('T')[0];
    
    if (dateStr === startStr) return 'start';
    if (dateStr === endStr) return 'end';
    return 'middle';
  };

  const openCreateModal = (date = null) => {
    setEditingEvent(null);
    const dateStr = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setFormData({
      title: '',
      description: '',
      event_type: 'task',
      start_date: `${dateStr}T09:00`,
      end_date: `${dateStr}T10:00`,
      all_day: false,
      color: EVENT_TYPES.task.color,
      attachments: [],
    });
    setShowEventModal(true);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      start_date: event.start_date?.slice(0, 16) || '',
      end_date: event.end_date?.slice(0, 16) || '',
      all_day: event.all_day || false,
      color: event.color || EVENT_TYPES[event.event_type]?.color || '#FFFFFF',
      attachments: event.attachments || [],
    });
    setShowEventModal(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Ingresa un título');
      return;
    }

    try {
      if (editingEvent) {
        await calendarApi.updateEvent(editingEvent.id, formData);
        toast.success('Evento actualizado');
      } else {
        await calendarApi.createEvent(formData);
        toast.success('Evento creado');
      }
      setShowEventModal(false);
      loadEvents();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (event) => {
    if (!confirm(`¿Eliminar "${event.title}"?`)) return;
    
    try {
      await calendarApi.deleteEvent(event.id);
      toast.success('Evento eliminado');
      loadEvents();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleToggleComplete = async (event) => {
    try {
      await calendarApi.toggleComplete(event.id);
      loadEvents();
    } catch (error) {
      console.error('Error toggling:', error);
    }
  };

  const addAttachment = () => {
    if (!newAttachment.name || !newAttachment.url) {
      toast.error('Completa nombre y URL');
      return;
    }
    setFormData({
      ...formData,
      attachments: [...formData.attachments, { ...newAttachment, id: Date.now() }]
    });
    setNewAttachment({ name: '', url: '', type: 'link' });
  };

  const removeAttachment = (id) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter(a => a.id !== id)
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  if (loading && events.length === 0) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-white" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 space-y-6"
          data-testid="calendar-page"
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <CalendarDays className="text-[#D4AF37]" size={28} />
                Calendario
              </h1>
              <p className="text-zinc-500 text-sm mt-1">Gestión de tareas, eventos y recordatorios</p>
            </div>
            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1 flex">
                {['month', 'list'].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1.5 text-sm font-bold uppercase tracking-wider rounded-md transition-colors ${
                      viewMode === mode ? 'bg-[#D4AF37] text-black' : 'text-[#71717A] hover:text-white'
                    }`}
                  >
                    {mode === 'month' ? 'Mes' : 'Lista'}
                  </button>
                ))}
              </div>
              <Button onClick={() => openCreateModal()} className="admin-btn-primary">
                <Plus className="mr-2" size={16} />
                Nuevo
              </Button>
            </div>
          </div>

          {/* Event Type Legend - Improved */}
          <div className="flex flex-wrap gap-3">
            {Object.entries(EVENT_TYPES).map(([key, { label, color, icon: TypeIcon }]) => (
              <motion.div 
                key={key} 
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#121212] border border-[#27272A]"
                whileHover={{ scale: 1.05, borderColor: color }}
              >
                <div 
                  className="w-2.5 h-2.5 rounded-full" 
                  style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}50` }} 
                />
                <span className="text-zinc-500 text-xs font-medium">{label}</span>
              </motion.div>
            ))}
          </div>

          {/* Calendar Navigation - Improved */}
          <div className="flex items-center justify-between admin-card !p-4">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigateMonth(-1)}
                className="hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] rounded-full"
              >
                <ChevronLeft size={20} />
              </Button>
              <motion.h2 
                key={`${currentDate.getMonth()}-${currentDate.getFullYear()}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-black uppercase min-w-[220px] text-center"
              >
                {MONTHS[currentDate.getMonth()]} <span className="text-[#52525B]">{currentDate.getFullYear()}</span>
              </motion.h2>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigateMonth(1)}
                className="hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] rounded-full"
              >
                <ChevronRight size={20} />
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => setCurrentDate(new Date())}
                className="admin-btn-secondary"
              >
                <CalendarDays size={16} className="mr-2" />
                Hoy
              </Button>
            </div>
          </div>

          {viewMode === 'month' ? (
            /* Calendar Grid - Improved Design */
            <div className="admin-card !p-0 overflow-hidden">
              {/* Day Headers */}
              <div className="grid grid-cols-7 bg-[#0A0A0A] border-b border-[#27272A]">
                {DAYS.map(day => (
                  <div key={day} className="py-4 text-center text-xs font-bold text-[#71717A] uppercase tracking-wider">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {calendarData.map((day, index) => {
                  const dayEvents = getEventsForDate(day.date);
                  const isCurrentDay = isToday(day.date);
                  const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
                  
                  return (
                    <motion.div
                      key={index}
                      className={`
                        min-h-[120px] border-b border-r border-[#1A1A1A] p-2 cursor-pointer
                        transition-all duration-200 relative group
                        ${!day.isCurrentMonth ? 'bg-[#0A0A0A]/70' : 'bg-[#0F0F0F]'}
                        ${isWeekend && day.isCurrentMonth ? 'bg-[#0D0D0D]' : ''}
                        hover:bg-[#1A1A1A] hover:z-10
                      `}
                      onClick={() => openCreateModal(day.date)}
                      whileHover={{ scale: 1.02, zIndex: 20 }}
                      transition={{ duration: 0.15 }}
                    >
                      {/* Day Number */}
                      <div className="flex items-center justify-between mb-2">
                        <div className={`
                          text-sm font-bold w-8 h-8 flex items-center justify-center rounded-full
                          transition-all duration-200
                          ${isCurrentDay 
                            ? 'bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                            : !day.isCurrentMonth 
                              ? 'text-[#3A3A3A]' 
                              : 'text-white/80 group-hover:bg-white/10'
                          }
                        `}>
                          {day.date.getDate()}
                        </div>
                        {/* Quick add button on hover */}
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileHover={{ scale: 1.1 }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Plus size={16} className="text-[#52525B] hover:text-white" />
                        </motion.div>
                      </div>
                      
                      {/* Events */}
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event) => {
                          const typeInfo = EVENT_TYPES[event.event_type] || EVENT_TYPES.task;
                          const isMultiDay = isMultiDayEvent(event);
                          const position = isMultiDay ? getEventPosition(event, day.date) : null;
                          const eventColor = event.color || typeInfo.color;
                          
                          // Special render for public platform events with image
                          if (event.isPublicEvent) {
                            const isPast = isEventPast(event);
                            return (
                              <motion.div
                                key={event.id}
                                className={`relative rounded-lg overflow-hidden cursor-pointer group ${isPast ? 'grayscale opacity-60' : ''}`}
                                onClick={(e) => { e.stopPropagation(); window.open(`/evento/${event.slug || event.id}`, '_blank'); }}
                                whileHover={{ scale: 1.03 }}
                                transition={{ duration: 0.15 }}
                              >
                                {/* Event image or gradient fallback */}
                                <div className="relative h-14 w-full">
                                  {event.image_url ? (
                                    <img 
                                      src={event.image_url} 
                                      alt={event.title}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className={`w-full h-full ${isPast ? 'bg-gradient-to-br from-zinc-600 to-zinc-700' : 'bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53]'}`} />
                                  )}
                                  {/* Overlay gradient */}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                                  
                                  {/* Event info */}
                                  <div className="absolute bottom-0 left-0 right-0 p-1.5">
                                    <p className="text-[10px] font-bold text-white truncate leading-tight drop-shadow-lg">
                                      {event.title}
                                    </p>
                                    {event.time && (
                                      <p className="text-[8px] text-white/70 truncate">
                                        {event.time} {event.venue_name ? `• ${event.venue_name}` : ''}
                                      </p>
                                    )}
                                  </div>
                                  
                                  {/* Status indicator */}
                                  <div className="absolute top-1 right-1">
                                    <span className={`text-[7px] font-bold px-1.5 py-0.5 ${isPast ? 'bg-zinc-500' : 'bg-[#FF6B6B]'} text-white rounded-full uppercase tracking-wider shadow-lg`}>
                                      {isPast ? 'Pasado' : 'Evento'}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          }
                          
                          // Regular calendar events (tasks, meetings, etc)
                          return (
                            <motion.div
                              key={event.id}
                              className={`
                                text-xs px-2 py-1.5 cursor-pointer
                                transition-all duration-200 hover:scale-[1.02]
                                ${event.completed ? 'line-through opacity-40' : ''}
                                ${isMultiDay 
                                  ? position === 'start' 
                                    ? 'rounded-l-md rounded-r-none mr-0' 
                                    : position === 'end'
                                      ? 'rounded-r-md rounded-l-none ml-0'
                                      : 'rounded-none mx-0'
                                  : 'rounded-md'
                                }
                              `}
                              style={{ 
                                backgroundColor: `${eventColor}25`, 
                                color: eventColor,
                                borderLeft: position !== 'middle' && position !== 'end' ? `3px solid ${eventColor}` : 'none',
                                marginLeft: position === 'middle' || position === 'end' ? '-8px' : '0',
                                marginRight: position === 'middle' || position === 'start' ? '-8px' : '0',
                                paddingLeft: position === 'middle' || position === 'end' ? '8px' : '6px',
                              }}
                              onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                              whileHover={{ backgroundColor: `${eventColor}40` }}
                            >
                              <div className="flex items-center gap-1.5 truncate">
                                {position === 'start' || !isMultiDay ? (
                                  <>
                                    {event.completed ? (
                                      <CheckCircle2 size={12} className="flex-shrink-0" />
                                    ) : (
                                      <Circle size={10} className="flex-shrink-0" style={{ fill: eventColor }} />
                                    )}
                                    <span className="truncate font-medium">{event.title}</span>
                                  </>
                                ) : (
                                  <span className="truncate font-medium opacity-70">↔</span>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <motion.div 
                            className="text-xs text-[#52525B] px-2 py-1 rounded bg-white/5 text-center"
                            whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                          >
                            +{dayEvents.length - 3} más
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* List View */
            <div className="space-y-2">
              {events.length === 0 ? (
                <div className="text-center py-16 text-[#52525B]">
                  <CalendarDays size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No hay eventos este mes</p>
                </div>
              ) : (
                events.sort((a, b) => new Date(a.start_date) - new Date(b.start_date)).map((event) => {
                  const typeInfo = EVENT_TYPES[event.event_type] || EVENT_TYPES.task;
                  const Icon = typeInfo.icon;
                  
                  return (
                    <motion.div
                      key={event.id}
                      className={`
                        bg-[#121212] border border-[#27272A] p-4 rounded-lg
                        flex items-center justify-between
                        ${event.completed ? 'opacity-50' : ''}
                      `}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => handleToggleComplete(event)}
                          className="flex-shrink-0"
                        >
                          {event.completed ? (
                            <CheckCircle2 className="text-green-500" size={20} />
                          ) : (
                            <Circle className="text-[#52525B] hover:text-white" size={20} />
                          )}
                        </button>
                        
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${event.color || typeInfo.color}20` }}
                        >
                          <Icon size={18} style={{ color: event.color || typeInfo.color }} />
                        </div>
                        
                        <div>
                          <p className={`font-medium ${event.completed ? 'line-through' : ''}`}>
                            {event.title}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                            <span>{new Date(event.start_date).toLocaleDateString('es-AR')}</span>
                            {!event.all_day && (
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {new Date(event.start_date).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            {event.attachments?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Paperclip size={12} />
                                {event.attachments.length}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(event)}>
                          <Edit2 size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(event)} className="hover:text-red-500">
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          )}

      {/* Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="text-blue-500" size={20} />
              {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Título del evento"
                className="bg-[#0A0A0A] border-[#27272A]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(v) => setFormData({ ...formData, event_type: v, color: EVENT_TYPES[v]?.color })}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="bg-[#0A0A0A] border-[#27272A] font-mono text-sm flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inicio</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>
              <div className="space-y-2">
                <Label>Fin</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.all_day}
                onCheckedChange={(checked) => setFormData({ ...formData, all_day: checked })}
              />
              <Label>Todo el día</Label>
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción o notas..."
                className="bg-[#0A0A0A] border-[#27272A] min-h-[80px]"
              />
            </div>

            {/* Attachments */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Paperclip size={14} />
                Archivos adjuntos
              </Label>
              
              {/* Existing attachments */}
              {formData.attachments.length > 0 && (
                <div className="space-y-2">
                  {formData.attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between bg-[#0A0A0A] border border-[#27272A] p-2 rounded">
                      <div className="flex items-center gap-2">
                        {att.type === 'drive' ? (
                          <FolderOpen size={16} className="text-yellow-500" />
                        ) : (
                          <Link2 size={16} className="text-blue-500" />
                        )}
                        <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline">
                          {att.name}
                        </a>
                      </div>
                      <button onClick={() => removeAttachment(att.id)} className="text-[#52525B] hover:text-red-500">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new attachment */}
              <div className="bg-[#0A0A0A] border border-[#27272A] p-3 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={newAttachment.name}
                    onChange={(e) => setNewAttachment({ ...newAttachment, name: e.target.value })}
                    placeholder="Nombre"
                    className="bg-[#121212] border-[#27272A] text-sm"
                  />
                  <Select value={newAttachment.type} onValueChange={(v) => setNewAttachment({ ...newAttachment, type: v })}>
                    <SelectTrigger className="bg-[#121212] border-[#27272A] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      <SelectItem value="link">Enlace</SelectItem>
                      <SelectItem value="drive">Google Drive</SelectItem>
                      <SelectItem value="excel">Excel/Sheets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newAttachment.url}
                    onChange={(e) => setNewAttachment({ ...newAttachment, url: e.target.value })}
                    placeholder="URL del archivo o carpeta"
                    className="bg-[#121212] border-[#27272A] text-sm flex-1"
                  />
                  <Button variant="outline" size="sm" onClick={addAttachment}>
                    <Plus size={14} />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-[#27272A]">
            {editingEvent && (
              <Button variant="destructive" onClick={() => { handleDelete(editingEvent); setShowEventModal(false); }}>
                <Trash2 className="mr-2" size={16} />
                Eliminar
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setShowEventModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} className="admin-btn-primary">
                {editingEvent ? 'Guardar cambios' : 'Crear evento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </motion.div>
        </AdminLayout>
      </SidebarProvider>
  );
};

export default CalendarPage;
